import type {
  FormProviderProps as RhFormProviderProps,
  FormProps as RhFormProps,
  SubmitErrorHandler,
  UseFormSetError,
  UseFormSetFocus,
  UseFormReturn,
  SubmitHandler,
  UseFormProps,
  FieldValues,
  Path,
} from 'react-hook-form';

import {
  FormProvider as RhFormProvider,
  useForm as useRhFrom,
} from 'react-hook-form';
import classNames from 'classnames';
import React from 'react';

export { useFormContext } from 'react-hook-form';

export type FormProviderProps<TFormValues extends FieldValues> = {
  onInvalid?: SubmitErrorHandler<TFormValues>;
  onSubmit?: SubmitHandler<TFormValues>;

  children: React.ReactNode;
  className?: string;
  id?: string;
} & Omit<RhFormProps<TFormValues>, 'onSubmit'> &
  RhFormProviderProps<TFormValues>;

export const FormProvider = <TFormValues extends FieldValues>({
  id,
  children,
  className,
  onInvalid,
  onSubmit,
  ...methods
}: FormProviderProps<TFormValues>) => {
  return (
    <RhFormProvider {...methods}>
      <form
        onSubmit={(e) => {
          e.stopPropagation();
          methods.handleSubmit(
            async (data, event) => {
              event?.preventDefault();
              if (onSubmit) {
                try {
                  const result = await onSubmit(
                    data as TFormValues['defaultValues'],
                    event,
                  );

                  // RESETTING IS FOR A SUBMIT THAT SUCCEEDED, and "did not
                  // throw" is not the same thing.
                  //
                  // A handler that reports failure the way React Hook Form
                  // invites — set the field errors, return — used to have those
                  // errors wiped by the reset a tick later, and the form left
                  // PRISTINE. `FormSubmitButton` disables itself once the values
                  // match the defaults, so the user got a greyed-out button, no
                  // message, and nothing saved. Measured on `ExercisePage`: an
                  // exercise with no equipment tag failed client-side zod and
                  // died exactly here, without ever reaching the upload queue.
                  //
                  // `=== false` and nothing else. Every existing handler returns
                  // `undefined`, and `undefined` has to keep meaning success or
                  // this quietly stops resetting every form in the app; an
                  // explicit `false` is the only way to opt into "I failed".
                  if (result !== false) {
                    methods.reset(data);
                  }
                } catch (error) {
                  // Swallowed so a rejected handler cannot become an unhandled
                  // rejection, but no longer SILENT: this used to be a bare
                  // `catch {}`, so a genuine failure (a 500, a dropped
                  // connection) left no trace anywhere — not in the console, not
                  // in Sentry. The form deliberately stays dirty.
                  // eslint-disable-next-line no-console
                  console.error('Form submit failed', error);
                }
              }
            },
            async (errors, event) => {
              event?.preventDefault();
              if (onInvalid) {
                await onInvalid(errors, event);
              }
            },
          )(e);
        }}
        onSubmitCapture={() => {
          methods?.clearErrors();
        }}
        className={classNames('h-full', className)}
        // zod (via the resolver) is the single source of truth for validation.
        // Without this the browser runs its own constraint validation first and
        // blocks submit with a native bubble — e.g. `type="email"`, which the
        // fields need for the `@` key on mobile keyboards, would reject a value
        // before the form's own message ever renders.
        noValidate
        id={id}
      >
        {children}
      </form>
    </RhFormProvider>
  );
};

/**
 * The React Hook Form path for one server error, with the segments the server
 * DID NOT SEND left out.
 *
 * `ZodFilter` reports `index: number | null` and `field: string | null`, and
 * the nulls are deliberate: an error on the ARRAY itself (`translations`
 * failing `.min(1)`) has no element to point at, and an element with no
 * sub-path (`tags.0`) has no field. Both used to be concatenated anyway, so the
 * error was registered under `translations.null.name` or `tags.0.null` — a path
 * no input holds. React Hook Form stored it, nothing rendered it, and the user
 * got a save that failed with no message anywhere on the page.
 *
 * It also takes the segments as a LIST because the nested branch below has a
 * `key` to include and used to drop it, addressing `weeks.0.name` for an error
 * the server reported under `weeks.days`.
 */
const fieldPath = (...segments: Array<null | number | string | undefined>) =>
  segments
    .filter((segment) => segment !== null && segment !== undefined)
    .map(String)
    .filter((segment) => segment.length > 0)
    .join('.');

/** The `{ message, index, field }` triple `ZodFilter` pushes for an array head. */
type FilterArrayEntry = {
  field: null | string;
  index: null | number;
  message: string;
};

const isFilterArrayEntry = (value: unknown): value is FilterArrayEntry =>
  typeof value === 'object' &&
  value !== null &&
  'message' in value &&
  'index' in value &&
  'field' in value;

/**
 * Walks a 422 body of ANY depth and emits one (path, message) per leaf.
 *
 * This replaces two hardcoded levels — the TODO that stood here — because the
 * server emits a THIRD shape those two never handled. `ZodFilter` treats a key
 * as an "array head" only when its second segment is numeric, so a nested
 * indexed path (`program.translations.1.name`, cited in the filter's own
 * docblock as a real observed path) is not one and goes through `_.set`.
 * lodash builds a SPARSE array for index 1, `JSON.stringify` renders the hole
 * as `null`, and the old array branch read `.index` off that `null` — a
 * TypeError thrown out of `setErrors`, so nothing was registered at all and the
 * user got a save that failed silently. The dense half was wrong too: an
 * element like `{name: 'Required'}` carries no `index`/`field`, so the path
 * collapsed onto the array itself with `message: undefined`.
 *
 * The two shapes are distinguished by CONTENT, not by position: an element that
 * carries all three of `message`/`index`/`field` is the filter's triple;
 * anything else is an ordinary nested node and is recursed into.
 */
const walkFormErrors = (
  node: unknown,
  path: string,
  emit: (path: string, message: string) => void,
) => {
  // A hole in a sparse array, or a field the server said nothing about.
  if (node === null || node === undefined) return;

  if (Array.isArray(node)) {
    node.forEach((entry, index) => {
      if (isFilterArrayEntry(entry)) {
        emit(fieldPath(path, entry.index, entry.field), entry.message);
        return;
      }
      walkFormErrors(entry, fieldPath(path, index), emit);
    });
    return;
  }

  if (typeof node === 'object') {
    Object.keys(node).forEach((key) =>
      walkFormErrors(
        (node as Record<string, unknown>)[key],
        fieldPath(path, key),
        emit,
      ),
    );
    return;
  }

  emit(path, String(node));
};

export function useFormErrors<TFieldValues extends FieldValues>(
  setError: UseFormSetError<TFieldValues>,
  setFocus?: UseFormSetFocus<TFieldValues>,
) {
  const setErrors = React.useCallback(
    (errors?: unknown) => {
      if (!errors || typeof errors !== 'object') return;

      walkFormErrors(errors, '', (path, message) => {
        const field = path as Path<TFieldValues>;
        setError(field, { message, type: 'manual' });
        setFocus?.(field);
      });
    },
    [setError, setFocus],
  );
  return { setErrors };
}

export function useForm<
  TFieldValues extends FieldValues = FieldValues,
  TContext extends object = object,
  TTransformedValues extends FieldValues | undefined = undefined,
>(
  args?: UseFormProps<TFieldValues, TContext>,
): UseFormReturn<TFieldValues, TContext, TTransformedValues> {
  return useRhFrom({
    mode: 'onSubmit',
    ...args,
    shouldFocusError: true,
  });
}
